import React from 'react'
import { PiArrowBendUpLeft, PiArrowBendDoubleUpLeft } from 'react-icons/pi'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { ReplyMode } from 'components/FormUtils/EmailRecipientSelect'

/**
 * Compact header row above the inline reply composer: a mode-switcher arrow
 * (Reply / Reply All / Forward), a "Replying to …" label, and an optional
 * trailing slot for Cc/Bcc toggles or similar.
 *
 * Shared by the company inbox and the campaign reply composer so both expose
 * the same mode switcher UX.
 */
export function InlineReplyHeader({
  mode,
  label,
  onModeChange,
  trailing,
}: {
  mode: ReplyMode
  label: string
  /** If omitted, the mode switcher renders disabled — useful for forward-only flows. */
  onModeChange?: (mode: ReplyMode) => void
  trailing?: React.ReactNode
}) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const ModeIcon =
    mode === 'reply_all' ? PiArrowBendDoubleUpLeft : PiArrowBendUpLeft

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={!onModeChange}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Change reply mode"
          >
            <ModeIcon className="size-4" />
          </button>
        </DropdownMenuTrigger>
        {onModeChange && (
          <DropdownMenuContent align="start" style={{ zIndex }}>
            <DropdownMenuItem onSelect={() => onModeChange('reply')}>
              Reply
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onModeChange('reply_all')}>
              Reply All
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onModeChange('forward')}>
              Forward
            </DropdownMenuItem>
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      <span className="text-sm font-medium text-neutral-800 truncate">
        {label}
      </span>
      {trailing}
    </div>
  )
}
